import React from "react";
import {
  Box,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Switch,
  useTheme,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { getNotificationMeta } from "./notificationCatalog";


const CHANNEL_ICONS = {
  email: MailOutlineIcon,
  slack: ChatBubbleOutlineIcon,
  teams: GroupsOutlinedIcon,
};

const getChannelIcon = (channelKey) =>
  CHANNEL_ICONS[channelKey] || NotificationsNoneOutlinedIcon;


const groupByTemplate = (rows) => {
  const grouped = [];
  const byTemplate = new Map();

  rows.forEach((row) => {
    let entry = byTemplate.get(row.template_key);
    if (!entry) {
      entry = { template_key: row.template_key, channels: [] };
      byTemplate.set(row.template_key, entry);
      grouped.push(entry);
    }
    entry.channels.push(row);
  });

  return grouped;
};


const ChannelToggle = ({ channel, subscribed, disabled, onChange }) => {
  const theme = useTheme();
  const Icon = getChannelIcon(channel.key);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ minWidth: 104 }}
    >
      <Icon
        sx={{
          fontSize: 16,
          color: subscribed
            ? theme.customColors.text.secondary
            : theme.customColors.text.disabled,
        }}
      />
      <Typography
        variant="body2"
        sx={{
          flexGrow: 1,
          fontSize: "0.8125rem",
          color: subscribed
            ? theme.customColors.text.strong
            : theme.customColors.text.secondary,
        }}
      >
        {channel.title}
      </Typography>
      <Switch
        size="small"
        checked={subscribed}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        inputProps={{ "aria-label": `${channel.title} notifications` }}
      />
    </Stack>
  );
};

/**
 * Shared presentation for the notification lists.
 *
 * One block per notification: name and description on the left, channel
 * subscriptions on the right, and — where the caller asks for it — a
 * frequency choice underneath.
 *
 * `rows` are already-resolved values from the server. This component never
 * recomputes company-vs-personal precedence.
 */
const NotificationGrid = ({
  rows = [],
  onToggleEnabled,
  onChangeCadence,
  cadences = [],
  showCadence = false,
  disabled = false,
  emptyMessage = "No notifications available.",
}) => {
  const theme = useTheme();

  if (!rows.length) {
    return (
      <Box
        sx={{
          textAlign: "center",
          py: 6,
          px: 4,
          borderRadius: "8px",
          border: `1px dashed ${theme.customColors.border.dashed}`,
          backgroundColor: theme.customColors.background.infoBox,
        }}
      >
        <NotificationsNoneOutlinedIcon
          sx={{ fontSize: 32, color: theme.customColors.text.disabled, mb: 1 }}
        />
        <Typography
          variant="body2"
          sx={{ color: theme.customColors.text.secondary }}
        >
          {emptyMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {groupByTemplate(rows).map((group, index) => {
        const meta = getNotificationMeta(group.template_key);
        const row = group.channels[0];
        const cadenceLocked =
          showCadence && (row.cadence_editable === false || row.is_locked);
        const anyEnabled = group.channels.some((item) => item.enabled);
        const cadenceTitle = cadences.find(
          (c) => c.key === row.cadence_key
        )?.title;

        return (
          <Box
            key={group.template_key}
            sx={{
              py: 2,
              borderTop:
                index === 0
                  ? "none"
                  : `1px solid ${theme.customColors.border.subtle}`,
            }}
          >
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
              justifyContent="space-between"
              spacing={{ xs: 1.5, sm: 3 }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: theme.customColors.text.strong,
                  }}
                >
                  {meta.label}
                </Typography>
                {meta.description && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.25,
                      fontSize: "0.8125rem",
                      lineHeight: 1.5,
                      color: theme.customColors.text.secondary,
                    }}
                  >
                    {meta.description}
                  </Typography>
                )}
              </Box>

              {/* One switch per channel, stacked so a second channel simply
                  adds a line instead of crowding the row. */}
              <Stack spacing={0.5} sx={{ flexShrink: 0, pt: 0.25 }}>
                {group.channels.map((channelRow) => {
                  const channel = {
                    key: channelRow.channel_key,
                    title: channelRow.channel_title || channelRow.channel_key,
                  };
                  const toggle = (
                    <ChannelToggle
                      channel={channel}
                      subscribed={Boolean(channelRow.enabled)}
                      disabled={disabled || channelRow.is_locked}
                      onChange={(next) => onToggleEnabled(channelRow, next)}
                    />
                  );

                  // A locked row is admin-controlled, so say why it is fixed.
                  return channelRow.is_locked ? (
                    <Tooltip
                      key={channelRow.channel_key}
                      title="Locked by your administrator"
                    >
                      <span>{toggle}</span>
                    </Tooltip>
                  ) : (
                    <React.Fragment key={channelRow.channel_key}>
                      {toggle}
                    </React.Fragment>
                  );
                })}
              </Stack>
            </Stack>

            {/* Frequency only matters while the notification is switched on.
                Sits in its own tinted band so it reads as a setting of this
                notification, not as more description text. */}
            {showCadence && anyEnabled && (
              <Box
                sx={{
                  mt: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "8px",
                  backgroundColor: theme.customColors.background.light,
                }}
              >
                {cadenceLocked ? (
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <LockOutlinedIcon
                      sx={{
                        fontSize: 15,
                        color: theme.customColors.text.iconMuted,
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.8125rem",
                        color: theme.customColors.text.secondary,
                      }}
                    >
                      {cadenceTitle ? (
                        <>
                          This notification is sent{" "}
                          <Box
                            component="span"
                            sx={{
                              fontWeight: 600,
                              color: theme.customColors.text.strong,
                            }}
                          >
                            {cadenceTitle.toLowerCase()}
                          </Box>
                         .
                        </>
                      ) : (
                        "This notification has no delivery schedule set."
                      )}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    sx={{ flexWrap: "wrap", rowGap: 1 }}
                  >
                    <Stack
                      direction="row"
                      spacing={0.75}
                      alignItems="center"
                      sx={{ minWidth: 132 }}
                    >
                      <ScheduleOutlinedIcon
                        sx={{
                          fontSize: 16,
                          color: theme.customColors.text.iconMuted,
                        }}
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8125rem",
                          fontWeight: 500,
                          color: theme.customColors.text.secondary,
                        }}
                      >
                        Send frequency
                      </Typography>
                    </Stack>

                    <FormControl size="small" sx={{ minWidth: 190 }}>
                      <Select
                        value={
                          cadences.some((c) => c.key === row.cadence_key)
                            ? row.cadence_key
                            : ""
                        }
                        displayEmpty
                        disabled={disabled || !cadences.length}
                        onChange={(event) =>
                          onChangeCadence(row, event.target.value)
                        }
                        renderValue={(value) =>
                          cadences.find((c) => c.key === value)?.title ??
                          "Choose frequency"
                        }
                        sx={{
                          fontSize: "0.8125rem",
                          borderRadius: "8px",
                          backgroundColor:
                            theme.customColors.background.main,
                          "& .MuiSelect-select": { py: 0.85 },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.customColors.border.subtle,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: theme.customColors.border.strong,
                          },
                        }}
                      >
                        {cadences.map((cadence) => (
                          <MenuItem
                            key={cadence.key}
                            value={cadence.key}
                            sx={{ fontSize: "0.8125rem" }}
                          >
                            {cadence.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                )}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default NotificationGrid;
